import type {
  PeriodDto, PeriodGranularity, SupplierLineStatus, SupplierOrderDto, SupplierOrderLineDto, SupplierOrderMode, SupplierOrderState,
} from '../../../../lib/api/v2/planning'

// ── Labels / pill classes (monochrome pills, OPS-001 UX pattern) ─────────────

export const LINE_STATUS_LABEL: Record<SupplierLineStatus, string> = {
  ToOrder: 'À commander', OnHold: 'En attente', Assigned: 'Commandée',
  Confirmed: 'Confirmée', Received: 'Reçue', Cancelled: 'Annulée',
}

// Blue stays in the two maps below on purpose (brand alignment 2026-08-29): it is a step in a
// monotone progression ramp (slate -> blue -> indigo -> emerald), not a primary action. Ink in the
// middle of the ramp would put the darkest tone first and break the ordering.
export const LINE_STATUS_CLASS: Record<SupplierLineStatus, string> = {
  ToOrder: 'border border-slate-400 bg-white text-slate-600',
  OnHold: 'border border-dashed border-amber-400 bg-amber-50 text-amber-700',
  Assigned: 'bg-blue-600 text-white',
  Confirmed: 'bg-indigo-700 text-white',
  Received: 'border border-emerald-600 bg-emerald-50 text-emerald-700',
  Cancelled: 'bg-muted text-muted-foreground line-through',
}

export const ORDER_STATUS_LABEL: Record<SupplierOrderState, string> = {
  Draft: 'Brouillon', Sent: 'Envoyée', Confirmed: 'Confirmée', Closed: 'Clôturée',
}

export const ORDER_STATUS_CLASS: Record<SupplierOrderState, string> = {
  Draft: 'border border-slate-400 bg-white text-slate-600',
  Sent: 'bg-blue-600 text-white',
  Confirmed: 'bg-indigo-700 text-white',
  Closed: 'border border-emerald-600 bg-emerald-50 text-emerald-700',
}

export const HOLD_KIND_LABEL = { Grouping: 'Achat groupé', Info: "Complément d'info" } as const
export const MODE_LABEL = { Email: 'Mail', Portal: 'Portail', Tradeplace: 'Tradeplace', Phone: 'Téléphone' } as const satisfies Record<SupplierOrderMode, string>
export const SUPPLIER_MODES = Object.keys(MODE_LABEL) as SupplierOrderMode[]

// ── Dates / periods ──────────────────────────────────────────────────────────

export function todayIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const dayMs = 86_400_000

function atUtc(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`)
}

function isoOf(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/** ISO-8601 week number (the week containing the first Thursday is W1). */
export function isoWeekNumber(dateIso: string): number {
  const d = atUtc(dateIso)
  const day = (d.getUTCDay() + 6) % 7 // Monday=0
  const thursday = new Date(d.getTime() + (3 - day) * dayMs)
  const firstThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4))
  const firstDay = (firstThursday.getUTCDay() + 6) % 7
  const week1Monday = new Date(firstThursday.getTime() - firstDay * dayMs)
  return Math.floor((thursday.getTime() - week1Monday.getTime()) / (7 * dayMs)) + 1
}

/** Builds a normalized PeriodDto from a granularity + any date inside it. */
export function periodFromInput(granularity: PeriodGranularity, dateIso: string): PeriodDto {
  if (granularity === 'Day') {
    return { start: dateIso, end: dateIso, granularity }
  }

  const d = atUtc(dateIso)
  const offset = (d.getUTCDay() + 6) % 7
  const monday = new Date(d.getTime() - offset * dayMs)
  return { start: isoOf(monday), end: isoOf(new Date(monday.getTime() + 6 * dayMs)), granularity }
}

const frDay = (iso: string) => {
  const [, m, d] = iso.split('-')
  return `${d}/${m}`
}

/** "17/06" for a day, "S25 · 15–21/06" for an ISO week. */
export function formatPeriod(p: PeriodDto): string {
  if (p.granularity === 'Day') return frDay(p.start)
  return `S${isoWeekNumber(p.start)} · ${p.start.split('-')[2]}–${frDay(p.end)}`
}

// ── Order / line projections ─────────────────────────────────────────────────

export interface GroupedLines {
  /** Lines living their own life (ToOrder / OnHold) — candidates for an order. */
  free: SupplierOrderLineDto[]
  /** Cancelled lines never attached to an order (history, collapsed). */
  cancelledFree: SupplierOrderLineDto[]
  /** This project's lines per order id. */
  byOrder: Map<string, SupplierOrderLineDto[]>
}

export function groupLines(lines: SupplierOrderLineDto[]): GroupedLines {
  const free: SupplierOrderLineDto[] = []
  const cancelledFree: SupplierOrderLineDto[] = []
  const byOrder = new Map<string, SupplierOrderLineDto[]>()

  for (const line of lines) {
    if (line.supplierOrderId) {
      const bucket = byOrder.get(line.supplierOrderId) ?? []
      bucket.push(line)
      byOrder.set(line.supplierOrderId, bucket)
    } else if (line.lineStatus === 'Cancelled') {
      cancelledFree.push(line)
    } else {
      free.push(line)
    }
  }

  return { free, cancelledFree, byOrder }
}

/** Header breakdown (OPS-001 #2): « À commander N · En attente N · Commandée N · Confirmée N · Reçue N ». */
export interface StatusCounts {
  toOrder: number
  onHold: number
  ordered: number
  confirmed: number
  received: number
  cancelled: number
}

export function computeStatusCounts(lines: SupplierOrderLineDto[]): StatusCounts {
  const c: StatusCounts = { toOrder: 0, onHold: 0, ordered: 0, confirmed: 0, received: 0, cancelled: 0 }
  for (const l of lines) {
    if (l.lineStatus === 'ToOrder') c.toOrder++
    else if (l.lineStatus === 'OnHold') c.onHold++
    else if (l.lineStatus === 'Assigned') c.ordered++
    else if (l.lineStatus === 'Confirmed') c.confirmed++
    else if (l.lineStatus === 'Received') c.received++
    else c.cancelled++
  }
  return c
}

/** Readiness (OPS-001 #6): received over everything still expected (cancelled lines left out). */
export function readiness(counts: StatusCounts): { received: number; total: number } {
  return { received: counts.received, total: counts.toOrder + counts.onHold + counts.ordered + counts.confirmed + counts.received }
}

/** A line is bulk-selectable when at least one toolbar gesture applies to it. */
export function isBulkEligible(line: SupplierOrderLineDto): boolean {
  return line.lineStatus === 'ToOrder' || line.lineStatus === 'OnHold'
}

/**
 * The order's next deadline — derived from lines (V102: period fields moved to lines).
 * Sent: earliest confirmationExpectedBy across Assigned lines, or latest desiredDeliveryPeriod.end.
 * Confirmed: latest confirmedDeliveryPeriod.end across Confirmed lines.
 */
export function orderNextDeadline(order: SupplierOrderDto, lines: SupplierOrderLineDto[]): { label: string; date: string } | null {
  if (order.status === 'Sent') {
    const deadlines = lines.filter((l) => l.lineStatus === 'Assigned' && l.confirmationExpectedBy).map((l) => l.confirmationExpectedBy!)
    if (deadlines.length > 0) return { label: 'Confirmation attendue', date: deadlines.reduce((a, b) => (a < b ? a : b)) }
    const desired = lines.filter((l) => l.desiredDeliveryPeriod).map((l) => l.desiredDeliveryPeriod!.end)
    if (desired.length > 0) return { label: 'Livraison souhaitée', date: desired.reduce((a, b) => (a > b ? a : b)) }
    return null
  }
  if (order.status === 'Confirmed') {
    const ends = lines.filter((l) => l.lineStatus === 'Confirmed' && l.confirmedDeliveryPeriod).map((l) => l.confirmedDeliveryPeriod!.end)
    if (ends.length > 0) return { label: 'Livraison', date: ends.reduce((a, b) => (a > b ? a : b)) }
    return null
  }
  return null
}

/**
 * Derives the effective display period for an order from its project lines (V102).
 * Returns the "worst-case" (latest end) period and whether it is confirmed.
 */
export function orderEffectivePeriod(lines: SupplierOrderLineDto[]): { period: PeriodDto; isConfirmed: boolean } | null {
  const confirmed = lines.filter((l) => l.confirmedDeliveryPeriod)
  if (confirmed.length > 0) {
    const rep = confirmed.reduce((a, b) => (a.confirmedDeliveryPeriod!.end >= b.confirmedDeliveryPeriod!.end ? a : b))
    return { period: rep.confirmedDeliveryPeriod!, isConfirmed: true }
  }
  const desired = lines.filter((l) => l.desiredDeliveryPeriod)
  if (desired.length > 0) {
    const rep = desired.reduce((a, b) => (a.desiredDeliveryPeriod!.end >= b.desiredDeliveryPeriod!.end ? a : b))
    return { period: rep.desiredDeliveryPeriod!, isConfirmed: false }
  }
  return null
}
